import { useState } from 'react';
import { Tabs, Table, Button, Dropdown, Checkbox } from 'ui';
import { MenuProps } from 'antd';
import dayjs from 'dayjs';
import { Coupon } from '../../../interfaces/Coupon';
import DiscountType from '../../../enums/discount-type';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useTranslations } from './hooks/use-translations';
import { useAuth } from 'hooks/useAuth';
import DefaultLayout from 'admin/components/DefaultLayout';
import CouponCreateUpdateDrawer from './CouponCreateUpdateDrawer';
import { DownOutlined } from '@ant-design/icons';

const CouponsPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [selectedEntry, setSelectedEntry] = useState<number>(0);
  const t = useTranslations();
  const { user } = useAuth();
  const farmId = user?.farmId;

  const { appendData: deleteCoupon } = useQueryApiClient({
    request: {
      url: 'api/v2/farms/' + farmId + '/coupons/:id',
      method: 'DELETE',
    },
    onSuccess: () => setRefreshKey((k) => k + 1),
  });

  const onClose = () => {
    setRefreshKey((k) => k + 1);
    setIsDrawerVisible(false);
    setSelectedEntry(0);
  };

  const columns = [
    { title: t('coupons.table.name'), dataIndex: 'name' },
    { title: t('coupons.table.coupon'), dataIndex: 'code' },
    {
      title: t('coupons.table.discount'),
      dataIndex: 'discount_value',
      render: (value: number, coupon: Coupon) => {
        return coupon.discount_type === DiscountType.PERCENT
          ? `– ${value}%`
          : `– ${value}€`;
      },
    },
    {
      title: t('coupons.table.start_date'),
      dataIndex: 'start_date',
      render: (_: string, coupon: Coupon) => {
        return coupon.start_date
          ? dayjs(coupon.start_date).format('DD.MM.YYYY')
          : '-';
      },
    },
    {
      title: t('coupons.table.end_date'),
      dataIndex: 'end_date',
      render: (_: string, coupon: Coupon) => {
        return coupon.end_date
          ? dayjs(coupon.end_date).format('DD.MM.YYYY')
          : '-';
      },
    },
    {
      title: t('coupons.table.status'),
      dataIndex: 'active',
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: t('coupons.table.used_count'),
      dataIndex: 'usage',
      render: (count: number) => `x ${count || 0}`,
    },
    {
      title: t('coupons.table.actions'),
      dataIndex: 'actions',
      render: (_: string, coupon: Coupon) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: t('general.edit'),
            onClick: () => {
              setSelectedEntry(coupon.id);
              setIsDrawerVisible(true);
            },
          },
          {
            key: 'delete',
            label: t('general.delete'),
            onClick: () => deleteCoupon([], { id: coupon.id }),
          },
        ];

        return (
          <Dropdown
            className="actions"
            menu={{ items }}
            trigger={['click']}
            key={coupon.id}
          >
            <Button
              className="action"
              label={t('general.actions')}
              icon={<DownOutlined />}
              iconPosition="end"
              size="middle"
              ability={'farm_edit'}
            />
          </Dropdown>
        );
      },
    },
  ];

  const items = [
    {
      key: 'coupons',
      label: t('navigation.coupons'),
      children: (
        <Table
          reload={refreshKey}
          url={`/api/v2/farms/${farmId}/coupons`}
          columns={columns}
          page_size={10}
        />
      ),
    },
  ];

  return (
    <div>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          { title: t('navigation.beginning') },
          { title: t('navigation.coupons') },
        ]}
      >
        <DefaultLayout.PageContent>
          <Tabs
            tabBarExtraContent={
              <Button
                type="primary"
                label={t('general.add')}
                onClick={() => setIsDrawerVisible(true)}
                ability={'farm_edit'}
              />
            }
            items={items}
          />
          <CouponCreateUpdateDrawer
            key={'coupon-drawer'}
            isVisible={isDrawerVisible}
            id={selectedEntry}
            onClose={onClose}
          />
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </div>
  );
};

export default CouponsPage;
